"use client";

import * as React from "react";
import { useState, useMemo } from "react";
import {
  type TherapeuticPresenceProps,
  type Scores,
  questions,
  catMeta,
} from "./types";

type SlideType = {
  type: "section";
  cat: string;
  questions: Array<{ index: number; q: string; reverse: boolean }>;
};

// Build slides: sections (with all questions)
function buildSlides(): SlideType[] {
  const slides: SlideType[] = [];
  const categories = ["immersion", "attunement", "emotional", "focus"];

  categories.forEach((cat) => {
    const catQuestions = questions
      .map((q, i) => ({ ...q, index: i }))
      .filter((q) => q.cat === cat)
      .map((q) => ({ index: q.index, q: q.q, reverse: q.reverse }));

    slides.push({ type: "section", cat, questions: catQuestions });
  });

  return slides;
}

const slides = buildSlides();
const totalQuestions = questions.length;

export const TherapeuticPresence = ({
  user,
  onComplete,
  apiEndpoint = "/api/tools/therapeutic-presence",
  historyUrl,
  source = "between-sessions",
}: TherapeuticPresenceProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentQuestionInSection, setCurrentQuestionInSection] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    new Array(totalQuestions).fill(null)
  );
  const [showResults, setShowResults] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  // Get current section index (0-3) based on current slide
  const currentSectionIndex = useMemo(() => {
    const slide = slides[currentSlide];
    if (!slide) return -1;
    if (slide.type === "section") {
      const catOrder = ["immersion", "attunement", "emotional", "focus"];
      return catOrder.indexOf(slide.cat);
    }
    return -1;
  }, [currentSlide]);

  const selectAnswer = (qIdx: number, val: number) => {
    const newAnswers = [...answers];
    newAnswers[qIdx] = val;
    setAnswers(newAnswers);

    const slide = slides[currentSlide];
    if (slide?.type === "section") {
      const totalInSection = slide.questions.length;
      if (currentQuestionInSection < totalInSection) {
        setAnimating(true);
        setTimeout(() => {
          setCurrentQuestionInSection((prev) => prev + 1);
          setAnimating(false);
        }, 300);
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nextSlide = async () => {
    if (animating) return;

    const slide = slides[currentSlide];
    if (!slide) return;

    const isLastSection = currentSlide === slides.length - 1;

    if (isLastSection) {
      if (!hasSaved) {
        setHasSaved(true);
        setAnimating(true);

        const answersObject = answers.reduce(
          (acc, val, idx) => {
            if (val !== null) acc[idx] = val;
            return acc;
          },
          {} as Record<number, number>
        );

        try {
          const response = await fetch(apiEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              type: "therapeutic-presence",
              firstName: user.firstName,
              email: user.email,
              answers: answersObject,
              notes: null,
              proId: user.id,
              source,
            }),
          });

          const data = await response.json();
          if (data.id) {
            setSavedId(data.id);
            if (onComplete) {
              onComplete(data.id);
            }
          }
        } catch (error) {
          console.error("Failed to save assessment:", error);
        }

        setShowResults(true);
        setAnimating(false);
        scrollToTop();
      }
      return;
    }

    setAnimating(true);
    setTimeout(() => {
      setCurrentSlide((prev) => Math.min(prev + 1, slides.length - 1));
      setCurrentQuestionInSection(0);
      setAnimating(false);
      scrollToTop();
    }, 150);
  };

  const goBack = () => {
    if (animating) return;

    const slide = slides[currentSlide];

    if (slide?.type === "section" && currentQuestionInSection > 0) {
      setAnimating(true);
      setTimeout(() => {
        setCurrentQuestionInSection((prev) => prev - 1);
        setAnimating(false);
      }, 150);
      return;
    }

    if (currentSlide > 0) {
      setAnimating(true);
      setTimeout(() => {
        const newSlide = currentSlide - 1;
        setCurrentSlide(newSlide);
        const prevSection = slides[newSlide];
        if (prevSection?.type === "section") {
          setCurrentQuestionInSection(prevSection.questions.length - 1);
        } else {
          setCurrentQuestionInSection(0);
        }
        setAnimating(false);
        scrollToTop();
      }, 150);
    }
  };

  const canGoBack = () => {
    return currentSlide > 0 || currentQuestionInSection > 0;
  };

  const resetTool = () => {
    setAnswers(new Array(totalQuestions).fill(null));
    setCurrentSlide(0);
    setCurrentQuestionInSection(0);
    setShowResults(false);
    setHasSaved(false);
    setSavedId(null);
    setNotes("");
    setNotesSaved(false);
    scrollToTop();
  };

  // Calculate scores
  const scores = useMemo((): Scores => {
    const catScores = {
      immersion: { total: 0, count: 0 },
      attunement: { total: 0, count: 0 },
      emotional: { total: 0, count: 0 },
      focus: { total: 0, count: 0 },
    };

    questions.forEach((q, i) => {
      const answer = answers[i];
      if (answer !== null && answer !== undefined) {
        const scoreVal = q.reverse ? 8 - answer : answer;
        const catData = catScores[q.cat as keyof typeof catScores];
        if (catData) {
          catData.total += scoreVal;
          catData.count += 1;
        }
      }
    });

    return {
      immersion:
        catScores.immersion.count > 0
          ? catScores.immersion.total / catScores.immersion.count
          : 0,
      attunement:
        catScores.attunement.count > 0
          ? catScores.attunement.total / catScores.attunement.count
          : 0,
      emotional:
        catScores.emotional.count > 0
          ? catScores.emotional.total / catScores.emotional.count
          : 0,
      focus:
        catScores.focus.count > 0
          ? catScores.focus.total / catScores.focus.count
          : 0,
    };
  }, [answers]);

  const overallScore = useMemo(() => {
    const validScores = Object.values(scores).filter((s) => s > 0);
    if (validScores.length === 0) return 0;
    return validScores.reduce((a, b) => a + b, 0) / validScores.length;
  }, [scores]);

  // Render current slide
  const renderSlide = () => {
    const slide = slides[currentSlide];
    if (!slide) return null;

    if (slide.type === "section") {
      const meta = catMeta[slide.cat] || { emoji: "", name: "", desc: "" };
      const totalInSection = slide.questions.length;
      const question = slide.questions[currentQuestionInSection];
      const savedVal = question ? answers[question.index] : null;
      const answeredInSection = slide.questions.filter(
        (q) => answers[q.index] !== null
      ).length;

      const showingCompletionScreen =
        currentQuestionInSection >= totalInSection &&
        answeredInSection === totalInSection;

      const sectionOrder = ["immersion", "attunement", "emotional", "focus"];
      const currentSectionIdx = sectionOrder.indexOf(slide.cat);
      const nextSectionCat = sectionOrder[currentSectionIdx + 1];
      const nextMeta = nextSectionCat ? catMeta[nextSectionCat] : null;

      if (showingCompletionScreen) {
        return (
          <div className="text-center py-4">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-8 h-8 bg-[#4A7C7C] rounded-full flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <span className="text-[#4A7C7C] font-medium">{meta.name}</span>
            </div>

            {nextMeta ? (
              <div className="bg-[#F8F5F2] rounded-xl p-6 text-left">
                <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-3">
                  Up next
                </p>
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{nextMeta.emoji}</div>
                  <div>
                    <p className="font-semibold text-[#2D2D2D] mb-1">
                      {nextMeta.name}
                    </p>
                    <p className="text-sm text-[#7A7A7A] leading-relaxed">
                      {nextMeta.desc}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-[#E8F2F2] rounded-xl p-6">
                <p className="text-2xl mb-2">✨</p>
                <p className="font-medium text-[#2E5B5B] mb-1">All done</p>
                <p className="text-sm text-[#4A7C7C]">
                  Your presence snapshot is ready.
                </p>
              </div>
            )}
          </div>
        );
      }

      if (question) {
        const isFirstQuestion =
          currentSlide === 0 && currentQuestionInSection === 0;

        return (
          <div className="text-center">
            {isFirstQuestion && (
              <div className="mb-6 pb-6 border-b border-[#E8E0D8]">
                <p className="text-[#2D2D2D] leading-relaxed mb-2">
                  Bring to mind a recent client session. For each statement,
                  rate how much it reflects your experience.
                </p>
                <p className="text-[#7A7A7A] text-sm">
                  Don't overthink it. First instinct is usually most honest.
                </p>
              </div>
            )}

            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E8F2F2] rounded-full text-sm font-medium text-[#2E5B5B] mb-6">
              {meta.emoji} {meta.name}
            </div>

            <div
              className={`transition-opacity duration-200 ${animating ? "opacity-0" : "opacity-100"}`}
            >
              <div className="flex justify-center gap-1.5 mb-6">
                {slide.questions.map((q, i) => (
                  <div
                    key={q.index}
                    className={`w-2 h-2 rounded-full transition-all ${
                      answers[q.index] !== null
                        ? "bg-[#4A7C7C]"
                        : i === currentQuestionInSection
                          ? "bg-[#4A7C7C]/40"
                          : "bg-[#E8E0D8]"
                    }`}
                  />
                ))}
              </div>

              <p className="text-[#2D2D2D] text-lg leading-relaxed mb-8 max-w-[400px] mx-auto">
                {question.q}
              </p>

              <div className="flex gap-2 justify-center max-w-[360px] mx-auto">
                {[1, 2, 3, 4, 5, 6, 7].map((val) => (
                  <button
                    key={val}
                    onClick={() => selectAnswer(question.index, val)}
                    className={`w-11 h-11 rounded-full font-medium text-sm transition-all ${
                      savedVal === val
                        ? "bg-[#4A7C7C] text-white scale-110"
                        : "bg-[#F8F5F2] text-[#7A7A7A] hover:bg-[#E8F2F2] hover:text-[#2E5B5B] hover:scale-105"
                    }`}
                  >
                    {val}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-[#aaa] max-w-[360px] mx-auto mt-2 px-1">
                <span>Not at all</span>
                <span>Completely</span>
              </div>
            </div>
          </div>
        );
      }

      return null;
    }

    return null;
  };

  const canProceed = () => {
    const slide = slides[currentSlide];
    if (!slide) return false;
    if (slide.type === "section") {
      const totalInSection = slide.questions.length;
      const allAnswered = slide.questions.every(
        (q) => answers[q.index] !== null
      );
      return currentQuestionInSection >= totalInSection && allAnswered;
    }
    return false;
  };

  const getInterpretation = (score: number) => {
    if (score >= 5.5)
      return {
        level: "high",
        color: "#4A7C7C",
        text: "Strong presence in this area.",
      };
    if (score >= 4)
      return {
        level: "mid",
        color: "#D4A574",
        text: "Moderate presence — some room for attention.",
      };
    return {
      level: "low",
      color: "#C4938A",
      text: "This area may need more attention.",
    };
  };

  // Results view
  if (showResults) {
    return (
      <div className="min-h-screen bg-[#F5F0EB] font-sans">
        <div className="py-16 px-6">
          <div className="max-w-[600px] mx-auto">
            <div className="text-center mb-10">
              <h1 className="font-serif text-3xl text-[#2D2D2D] mb-3">
                Your Presence Snapshot
              </h1>
              <p className="text-[#7A7A7A]">
                Based on your responses, here's how your therapeutic presence
                looked in that session.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-[0_2px_20px_rgba(0,0,0,0.06)] mb-6">
              <div className="text-center mb-8 pb-8 border-b border-[#E8E0D8]">
                <div className="text-5xl font-serif text-[#4A7C7C] mb-2">
                  {overallScore.toFixed(1)}
                </div>
                <p className="text-sm text-[#7A7A7A]">
                  Overall Presence Score (out of 7)
                </p>
              </div>

              <div className="space-y-6">
                {(
                  ["immersion", "attunement", "emotional", "focus"] as const
                ).map((cat) => {
                  const score = scores[cat];
                  const meta = catMeta[cat] || { emoji: "", name: "", desc: "" };
                  const interp = getInterpretation(score);
                  const percent = (score / 7) * 100;

                  return (
                    <div key={cat}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{meta.emoji}</span>
                          <span className="font-medium text-[#2D2D2D]">
                            {meta.name}
                          </span>
                        </div>
                        <span className="font-semibold text-[#2D2D2D]">
                          {score.toFixed(1)}
                        </span>
                      </div>
                      <div className="h-2 bg-[#E8E0D8] rounded-full overflow-hidden mb-2">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${percent}%`,
                            backgroundColor: interp.color,
                          }}
                        />
                      </div>
                      <p className="text-xs text-[#7A7A7A]">{interp.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-[#E8F2F2] rounded-2xl p-6 mb-6">
              <h3 className="font-serif text-lg text-[#2E5B5B] mb-3">
                What this might mean
              </h3>
              <div className="text-sm text-[#2E5B5B] leading-relaxed space-y-3">
                {overallScore >= 5.5 ? (
                  <p>
                    You were deeply present across all dimensions — grounded in
                    yourself while fully available to your client. Notice what
                    conditions supported this.
                  </p>
                ) : overallScore >= 4 ? (
                  <p>
                    Your presence was steady across most dimensions. The value
                    of this tool is in tracking patterns over time — notice how
                    your presence shifts across different sessions.
                  </p>
                ) : (
                  <p>
                    These scores are information, not a verdict. Presence
                    naturally fluctuates — it varies by session, by day, by
                    what's happening in your life.
                  </p>
                )}

                {scores.focus < 4 && (
                  <p>
                    Focus and sustained attention scored lower. Mind-wandering
                    is normal — the question is what's pulling your attention
                    away.
                  </p>
                )}

                {scores.emotional < 4 && (
                  <p>
                    Emotional availability was limited. You may have felt a
                    barrier between you and your client, or noticed your outward
                    responses not matching your inner experience.
                  </p>
                )}

                {scores.attunement < 4 && (
                  <p>
                    Attunement scored lower this session. Consider what might
                    have made it harder to sense into your client's experience.
                  </p>
                )}

                {scores.immersion < 4 && (
                  <p>
                    Immersion was limited. Something may have been keeping you
                    at the edges of the session rather than fully entering it.
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 mb-6 border border-[#E8E0D8]">
              <h3 className="font-medium text-[#2D2D2D] mb-3">
                Questions to sit with
              </h3>
              <ul className="text-sm text-[#7A7A7A] space-y-2">
                <li>• What was going on in your life before this session?</li>
                {scores.focus < 4 && (
                  <li>• If your mind wandered, what was it drawn to?</li>
                )}
                {scores.emotional < 4 && (
                  <li>
                    • Was there something that felt hard to express in the room?
                  </li>
                )}
                {scores.attunement < 4 && (
                  <li>
                    • What might have made it harder to sense into your client's
                    world?
                  </li>
                )}
                {scores.immersion < 4 && (
                  <li>
                    • What might have been keeping you at the edges of the
                    session?
                  </li>
                )}
                {scores.focus >= 4 &&
                  scores.emotional >= 4 &&
                  scores.attunement >= 4 &&
                  scores.immersion >= 4 && (
                    <li>• What helped you be so present today?</li>
                  )}
                <li>• What would help you arrive more fully next time?</li>
              </ul>
            </div>

            {/* Notes Section */}
            <div className="bg-white rounded-2xl p-6 mb-6 border border-[#E8E0D8]">
              <h3 className="font-medium text-[#2D2D2D] mb-2">
                Your Reflections
              </h3>
              <p className="text-sm text-[#7A7A7A] mb-4">
                Capture any thoughts or insights while they're fresh. This is just for you.
              </p>
              <textarea
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setNotesSaved(false);
                }}
                placeholder="What stood out to you about this session? Any patterns you're noticing?"
                className="w-full p-4 text-sm border border-[#E8E0D8] rounded-xl resize-none focus:border-[#4A7C7C] focus:ring-1 focus:ring-[#4A7C7C]/20 outline-none transition-colors"
                rows={4}
              />
              {notes.trim() && savedId && (
                <div className="mt-3 flex items-center justify-end gap-3">
                  {notesSaved && (
                    <span className="text-xs text-[#4A7C7C] flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Saved
                    </span>
                  )}
                  <button
                    onClick={async () => {
                      if (!savedId) return;
                      setIsSavingNotes(true);
                      try {
                        await fetch(`${apiEndpoint}/${savedId}`, {
                          method: "PATCH",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ notes: notes.trim() }),
                        });
                        setNotesSaved(true);
                      } catch (error) {
                        console.error("Failed to save notes:", error);
                      } finally {
                        setIsSavingNotes(false);
                      }
                    }}
                    disabled={isSavingNotes || notesSaved}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      notesSaved
                        ? "bg-[#E8F2F2] text-[#4A7C7C] cursor-default"
                        : "bg-[#4A7C7C] text-white hover:bg-[#2E5B5B]"
                    } disabled:opacity-50`}
                  >
                    {isSavingNotes ? "Saving..." : notesSaved ? "Saved" : "Save Notes"}
                  </button>
                </div>
              )}
            </div>

            <div className="text-center mt-8">
              <button
                onClick={resetTool}
                className="text-sm text-[#7A7A7A] hover:text-[#4A7C7C] transition-colors"
              >
                Start over with a different session
              </button>

              {historyUrl && (
                <div className="mt-6">
                  <a
                    href={historyUrl}
                    className="inline-flex items-center gap-2 text-sm text-[#4A7C7C] hover:text-[#2E5B5B] transition-colors"
                  >
                    View all my results
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              )}

              <p className="text-[10px] text-[#bbb] mt-6">
                Based on the Therapeutic Presence Inventory by Dr. Shari Geller
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tool interface
  return (
    <div className="min-h-screen bg-[#F5F0EB] font-sans">
      <div className="py-16 px-6">
        <div className="max-w-[560px] mx-auto">
          <div className="text-center mb-6">
            <h1 className="font-serif text-2xl text-[#2D2D2D]">
              Therapeutic Presence Self-Reflection
            </h1>
          </div>

          {currentSectionIndex >= 0 && currentSectionIndex < 4 && (
            <div className="mb-8">
              <div className="flex justify-center gap-2 mb-3">
                {["immersion", "attunement", "emotional", "focus"].map(
                  (cat, i) => (
                    <div
                      key={cat}
                      className={`w-10 h-1.5 rounded-full transition-all ${
                        currentSectionIndex > i
                          ? "bg-[#4A7C7C]"
                          : currentSectionIndex === i
                            ? "bg-[#4A7C7C]/60"
                            : "bg-[#E8E0D8]"
                      }`}
                    />
                  )
                )}
              </div>
              <p className="text-xs text-center text-[#999]">
                Section {currentSectionIndex + 1} of 4
              </p>
            </div>
          )}

          <div
            className={`bg-white rounded-2xl shadow-[0_2px_24px_rgba(0,0,0,0.06)] p-8 transition-opacity duration-150 ${
              animating ? "opacity-50" : "opacity-100"
            }`}
          >
            {renderSlide()}
          </div>

          <div className="flex justify-between items-center mt-8">
            <div>
              {canGoBack() && (
                <button
                  onClick={goBack}
                  className="text-sm text-[#7A7A7A] hover:text-[#4A7C7C] transition-all"
                >
                  ← Back
                </button>
              )}
            </div>

            <div>
              {canProceed() && (
                <button
                  onClick={nextSlide}
                  className="px-8 py-3 rounded-full font-semibold text-sm bg-[#4A7C7C] text-white hover:bg-[#2E5B5B] hover:-translate-y-0.5 transition-all"
                >
                  {currentSlide === slides.length - 1
                    ? "See My Results →"
                    : "Continue →"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
